import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Search, ExternalLink, Package, Layers, Globe, ShoppingCart, InfoIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import StauffStockModal from './StauffStockModal';
import { logCustomerSearch as writeSearchLog, buildResultsSnapshot } from '../utils/searchLogger';

// Debounce utility function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const AdvancedSearch = ({ onResultSelect, onAddToCart, userId, accountData }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [includeStauff, setIncludeStauff] = useState(false);
  const [results, setResults] = useState([]);
  const [stauffSuggestions, setStauffSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stauffLoading, setStauffLoading] = useState(false);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [selectedPartNumber, setSelectedPartNumber] = useState('');
  
  // Autocomplete state
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const autocompleteRef = useRef(null);

  // Enhanced search logging for customer analytics
  const logCustomerSearch = useCallback((searchTerm, searchType, searchSuccess, resultsCount = 0, results = []) => (
    writeSearchLog({
      userId,
      account: accountData,
      searchTerm,
      searchType,
      success: searchSuccess,
      resultsCount,
      source: 'advanced_search',
      results: buildResultsSnapshot(results),
    })
  ), [userId, accountData]);

  // Autocomplete handler with debouncing
  const handleAutocomplete = useCallback(
    debounce(async (query) => {
      if (query.length < 2) {
        setAutocompleteSuggestions([]);
        setShowAutocomplete(false);
        return;
      }

      setAutocompleteLoading(true);
      try {
        const response = await fetch(
          `https://api.mhsonline.au/api/autocomplete/?q=${encodeURIComponent(query)}&limit=10`,
          {
            method: 'GET',
            headers: { 'Accept': 'application/json' },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setAutocompleteSuggestions(data.suggestions || []);
          setShowAutocomplete(data.suggestions && data.suggestions.length > 0);
          setSelectedSuggestionIndex(-1);
        } else {
          setAutocompleteSuggestions([]);
          setShowAutocomplete(false);
        }
      } catch (error) {
        console.error('Autocomplete error:', error);
        setAutocompleteSuggestions([]);
        setShowAutocomplete(false);
      } finally {
        setAutocompleteLoading(false);
      }
    }, 300),
    []
  );

  // Click outside handler to close autocomplete
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation handler
  const handleKeyDown = (e) => {
    if (!showAutocomplete || autocompleteSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < autocompleteSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        if (selectedSuggestionIndex >= 0) {
          e.preventDefault();
          const selected = autocompleteSuggestions[selectedSuggestionIndex];
          setSearchTerm(selected.part_number);
          setShowAutocomplete(false);
          setSelectedSuggestionIndex(-1);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowAutocomplete(false);
        setSelectedSuggestionIndex(-1);
        break;
      default:
        break;
    }
  };

  // Suggestion click handler
  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.part_number);
    setShowAutocomplete(false);
    setSelectedSuggestionIndex(-1);
  };

  const handleSearch = useCallback(async (e) => {
    e.preventDefault();
    
    // Validate search term
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    
    setLoading(true);
    setResults([]);
    setStauffSuggestions(null);
    setShowAutocomplete(false);

    try {
      // Use Elasticsearch for fast search (searches both part numbers AND descriptions)
      const baseUrl = `https://api.mhsonline.au/api/search/?search_term=${encodeURIComponent(searchTerm)}&limit=50`;
      
      // Search both fuzzy (part numbers) and description in parallel
      const [fuzzyResponse, descResponse] = await Promise.all([
        fetch(`${baseUrl}&search_type=fuzzy`),
        fetch(`${baseUrl}&search_type=description`)
      ]);

      if (fuzzyResponse.ok && descResponse.ok) {
        // Both searches succeeded
        const fuzzyData = await fuzzyResponse.json();
        const descData = await descResponse.json();

        // Combine results and remove duplicates by prod_code
        const combined = [...fuzzyData, ...descData];
        const uniqueResults = combined.filter((item, index, self) =>
          index === self.findIndex(t => t.prod_code === item.prod_code)
        );

        setResults(uniqueResults);

        // Log the search
        await logCustomerSearch(searchTerm, 'fuzzy', uniqueResults.length > 0, uniqueResults.length, uniqueResults);

        if (uniqueResults.length === 0 && !includeStauff) {
          toast.info('No results found');
        }
      } else {
        throw new Error('Elasticsearch search failed');
      }
    } catch (elasticError) {
      console.warn('Elasticsearch search failed, falling back to legacy search:', elasticError);
      
      // Fallback to legacy /api/enquire/ endpoint
      try {
        const fallbackResponse = await fetch('/api/enquire/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ searchTerm, searchType: 'fuzzy' }),
        });

        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          const resultArray = Array.isArray(fallbackData) ? fallbackData : [];
          setResults(resultArray);
          
          // Log the fallback search
          await logCustomerSearch(searchTerm, 'fuzzy', resultArray.length > 0, resultArray.length, resultArray);
          
          if (resultArray.length === 0 && !includeStauff) {
            toast.info('No results found');
          }
        }
      } catch (fallbackError) {
        toast.error(`Search failed: ${fallbackError.message}`);
        console.error('Fallback search error:', fallbackError);
        // Log failed search
        await logCustomerSearch(searchTerm, 'fuzzy', false, 0);
      }
    }

    // Handle Stauff search if enabled
    if (includeStauff && searchTerm.trim()) {
      setStauffLoading(true);
      try {
        const stauffResponse = await fetch('/api/stauff/search', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ partNumber: searchTerm }),
        });

        if (stauffResponse.ok) {
          const stauffData = await stauffResponse.json();
          if (stauffData.success) {
            setStauffSuggestions(stauffData.suggestions);
          }
        }
      } catch (stauffError) {
        console.warn('Stauff search failed:', stauffError);
      } finally {
        setStauffLoading(false);
      }
    }

    setLoading(false);
  }, [searchTerm, includeStauff, onResultSelect]);

  const handleResultClick = useCallback((result) => {
    onResultSelect(result.prod_code);
  }, [onResultSelect]);

  const handleStauffProductClick = useCallback((product) => {
    setSelectedPartNumber(product.id);
    setStockModalOpen(true);
  }, []);

  const handleExternalLinkClick = useCallback((url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <>
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-2xl font-bold text-brand-dark mb-4">Advanced Search</h2>
        
        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-4">
          <div className="flex items-center mb-4">
            <div className="flex-grow relative" ref={autocompleteRef}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleAutocomplete(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (searchTerm.length >= 2 && autocompleteSuggestions.length > 0) {
                    setShowAutocomplete(true);
                  }
                }}
                placeholder="Search part numbers or descriptions..."
                className="w-full px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              
              {/* Autocomplete Dropdown */}
              {showAutocomplete && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 right-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto mt-1"
                >
                  {autocompleteLoading ? (
                    <div className="px-4 py-3 text-center">
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      <span className="ml-2 text-sm text-gray-600">Loading suggestions...</span>
                    </div>
                  ) : autocompleteSuggestions.length > 0 ? (
                    <ul>
                      {autocompleteSuggestions.map((suggestion, index) => (
                        <li
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className={`px-4 py-2 hover:bg-gray-50 cursor-pointer border-l-4 transition-colors ${
                            index === selectedSuggestionIndex 
                              ? 'bg-blue-50 border-blue-500' 
                              : 'border-transparent'
                          }`}
                        >
                          <div className="font-medium text-gray-900">{suggestion.part_number}</div>
                          <div className="text-sm text-gray-600">{suggestion.description}</div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      No suggestions found
                    </div>
                  )}
                </motion.div>
              )}
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              {loading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <Search className="w-5 h-5" />
                </motion.div>
              ) : (
                <Search className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Search Options */}
          <div className="flex items-center justify-between mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeStauff}
                onChange={(e) => setIncludeStauff(e.target.checked)}
                className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Include Stauff suggestions</span>
            </label>
            
            {/* Powered by Elasticsearch badge */}
            <div className="flex items-center text-xs text-gray-500">
              <span className="mr-2">Powered by</span>
              <svg viewBox="0 0 24 24" className="h-4 w-4 mr-1" fill="#00BFB3">
                <path d="M20.345 14.828c.802 0 1.455-.653 1.455-1.455 0-.803-.653-1.456-1.455-1.456H13.11l3.247-5.626c.402-.695.163-1.584-.531-1.986-.695-.402-1.584-.163-1.986.531l-3.247 5.626V3.655c0-.802-.653-1.455-1.455-1.455-.803 0-1.456.653-1.456 1.455v6.807L4.435 4.836c-.402-.694-1.291-.933-1.986-.531-.694.402-.933 1.291-.531 1.986l3.247 5.626H3.655c-.802 0-1.455.653-1.455 1.456 0 .802.653 1.455 1.455 1.455h6.51l-3.247 5.626c-.402.695-.163 1.584.531 1.986.694.402 1.584.163 1.986-.531l3.247-5.626v6.807c0 .802.653 1.455 1.456 1.455.802 0 1.455-.653 1.455-1.455v-6.807l3.247 5.626c.402.694 1.291.933 1.986.531.694-.402.933-1.291.531-1.986l-3.247-5.626h6.735z"/>
              </svg>
              <span className="font-semibold text-gray-600">Elasticsearch</span>
            </div>
          </div>
        </form>

        {/* Internal Search Results */}
        {results.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Package className="w-5 h-5 mr-2 text-brand-dark" />
              Internal Search Results ({results.length})
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <ul className="divide-y divide-gray-200">
                {results.map((result, index) => (
                  <li
                    key={index}
                    className="py-3 cursor-pointer hover:bg-white rounded px-3 transition-colors"
                    onClick={() => handleResultClick(result)}
                  >
                    <p className="font-medium text-brand-dark">{result.prod_code}</p>
                    <p className="text-sm text-gray-600">{result.Description}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Stauff Suggestions */}
        {includeStauff && (
          <div className="space-y-4">
            {stauffLoading && (
              <div className="flex items-center justify-center py-8">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <Search className="w-8 h-8 text-brand" />
                </motion.div>
                <span className="ml-3 text-gray-600">Searching Stauff suggestions...</span>
              </div>
            )}

            {stauffSuggestions && (
              <div className="space-y-6">
                {/* Stauff Products */}
                {stauffSuggestions.products.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <Package className="w-5 h-5 mr-2 text-orange-600" />
                      Stauff Products ({stauffSuggestions.products.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {stauffSuggestions.products.map((product, index) => (
                        <div
                          key={index}
                          className="bg-orange-50 border border-orange-200 rounded-lg p-4 cursor-pointer hover:bg-orange-100 transition-colors"
                          onClick={() => handleStauffProductClick(product)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-grow">
                              <p className="font-medium text-orange-800">{product.displayName}</p>
                              {product.category && (
                                <p className="text-sm text-orange-600">{product.category}</p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStauffProductClick(product);
                                }}
                                className="text-orange-600 hover:text-orange-800"
                                title="Check Stock"
                              >
                                <ShoppingCart className="w-4 h-4" />
                              </button>
                              {product.productUrl && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleExternalLinkClick(product.productUrl);
                                  }}
                                  className="text-orange-600 hover:text-orange-800"
                                  title="View on Stauff website"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stauff Categories */}
                {stauffSuggestions.categories.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <Layers className="w-5 h-5 mr-2 text-blue-600" />
                      Related Categories ({stauffSuggestions.categories.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {stauffSuggestions.categories.map((category, index) => (
                        <div
                          key={index}
                          className="bg-blue-50 border border-blue-200 rounded-lg p-3 cursor-pointer hover:bg-blue-100 transition-colors"
                          onClick={() => handleExternalLinkClick(category.url)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-blue-800">{category.name}</span>
                            <ExternalLink className="w-4 h-4 text-blue-600" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stauff Sites/Content */}
                {stauffSuggestions.sites.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <Globe className="w-5 h-5 mr-2 text-green-600" />
                      Related Content ({stauffSuggestions.sites.length})
                    </h3>
                    <div className="space-y-2">
                      {stauffSuggestions.sites.map((site, index) => (
                        <div
                          key={index}
                          className="bg-green-50 border border-green-200 rounded-lg p-3 cursor-pointer hover:bg-green-100 transition-colors"
                          onClick={() => handleExternalLinkClick(site.url)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-green-800">{site.name}</span>
                            <ExternalLink className="w-4 h-4 text-green-600" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Enquiry List Option */}
                {stauffSuggestions.enquiryList && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-800">Not finding what you need?</h4>
                        <p className="text-sm text-gray-600">
                          Add "{stauffSuggestions.enquiryList.searchTerm}" to Stauff enquiry list
                        </p>
                      </div>
                      <button
                        onClick={() => handleExternalLinkClick(stauffSuggestions.enquiryList.url)}
                        className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 flex items-center"
                      >
                        <InfoIcon className="w-4 h-4 mr-2" />
                        Add to Enquiry
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stauff Stock Modal */}
      <StauffStockModal
        isOpen={stockModalOpen}
        onClose={() => setStockModalOpen(false)}
        partNumber={selectedPartNumber}
        autoCheck={true}
        onAddToCart={onAddToCart}
      />
    </>
  );
};

export default AdvancedSearch;
